/**
 * The board on a phone (#39): the board as large as the screen allows, the
 * tools in a strip, the phases in a bar, and everything used now and then —
 * ink, setup, recording, export — in a sheet behind four tabs.
 *
 * Portrait and landscape are this one component arranged two ways by CSS, so
 * turning the phone never remounts the board or interrupts a recording.
 */

import { useEffect, useRef, useState, type CSSProperties, type JSX, type ReactNode, type RefObject } from 'react';
import { Board } from '../Board.tsx';
import { RecordPanel } from '../RecordPanel.tsx';
import { SavedPlays } from '../SavedPlays.tsx';
import { DeletePhaseButton, PhaseChips, Scrubber, StepButton } from '../TimelineBar.tsx';
import {
  ExportControls,
  HistoryButtons,
  InkControls,
  PackHint,
  SetupControls,
  ToolButtons,
  type ExportActions,
} from '../Toolbar.tsx';
import type { BoardApi } from '../useBoard.ts';
import type { FormationsApi } from '../useFormations.ts';
import type { SavedPlaysApi } from '../useSavedPlays.ts';
import { formatClock, type RecordingApi } from '../useRecording.ts';
import type { BoardFrame } from '../recordingFrame.ts';
import type { TutorialApi } from '../tutorial/useTutorial.ts';
import type { SheetTab } from '../layout.ts';
import { term } from '../../core/index.ts';

const TABS: readonly { readonly id: SheetTab; readonly label: string }[] = [
  { id: 'ink', label: 'Ink' },
  { id: 'setup', label: 'Setup' },
  { id: 'record', label: 'Record' },
  { id: 'more', label: 'More' },
];

/** How far the sheet has to be dragged down, in CSS pixels, to close. */
const SWIPE_CLOSE_PX = 60;

interface PhoneWorkspaceProps extends ExportActions {
  readonly api: BoardApi;
  readonly formations: FormationsApi;
  readonly plays: SavedPlaysApi;
  readonly recording: RecordingApi;
  readonly tutorial: TutorialApi;
  readonly developer: boolean;
  readonly canvasRef: RefObject<HTMLCanvasElement | null>;
  readonly onFrame?: (frame: BoardFrame) => void;
}

export function PhoneWorkspace({
  api,
  formations,
  plays,
  recording,
  tutorial,
  developer,
  canvasRef,
  onFrame,
  ...exports
}: PhoneWorkspaceProps): JSX.Element {
  const [tab, setTab] = useState<SheetTab | null>(null);
  const toggle = (next: SheetTab): void => setTab((open) => (open === next ? null : next));

  // The tutorial opens the tab a step's control lives in, and closes the
  // sheet again for a step on the board or the bars.
  const step = tutorial.run ? tutorial.run.steps[tutorial.run.index] : undefined;
  const tutorialWasUp = useRef(false);
  useEffect(() => {
    if (step) {
      tutorialWasUp.current = true;
      setTab(step.sheet ?? null);
    } else if (tutorialWasUp.current) {
      tutorialWasUp.current = false;
      setTab(null);
    }
  }, [step]);

  // A finished video waits in the Record tab; say so if the coach is elsewhere.
  // Only when the coach is elsewhere: watching it finish in the Record tab is
  // seeing it already, so the pill must not appear once they close the sheet.
  const [videoReady, setVideoReady] = useState(false);
  const wasPreparing = useRef(false);
  const tabRef = useRef(tab);
  tabRef.current = tab;
  useEffect(() => {
    if (recording.phase === 'preparing') wasPreparing.current = true;
    else if (wasPreparing.current) {
      wasPreparing.current = false;
      if (recording.result && tabRef.current !== 'record') setVideoReady(true);
    }
  }, [recording.phase, recording.result]);
  useEffect(() => {
    if (tab === 'record') setVideoReady(false);
  }, [tab]);

  // Escape closes the sheet, for a phone with a keyboard attached.
  useEffect(() => {
    if (tab === null) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setTab(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tab]);

  // The sheet stops at the tabs — above them upright, beside them sideways —
  // so they stay in reach to switch pages or close it.
  const tabsRef = useRef<HTMLElement | null>(null);
  const [clear, setClear] = useState<{ bottom: number; right: number }>({ bottom: 0, right: 0 });
  useEffect(() => {
    const tabs = tabsRef.current;
    if (!tabs) return;
    const measure = (): void => {
      const rect = tabs.getBoundingClientRect();
      const sideways = window.innerWidth > window.innerHeight;
      setClear(
        sideways
          ? { bottom: 0, right: Math.max(0, window.innerWidth - rect.left) }
          : { bottom: Math.max(0, window.innerHeight - rect.top), right: 0 },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(tabs);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const phaseWord = term(api.pack, 'play.keyframe', 'Phase');

  return (
    <>
      <main className="phone">
        <div className="phone__board">
          <Board api={api} canvasRef={canvasRef} onFrame={onFrame} />
          <RecordingPill recording={recording} visible={tab !== 'record'} videoReady={videoReady} onOpen={() => setTab('record')} />
        </div>

        <div className="tool-strip" role="toolbar" aria-label="Tools">
          <ToolButtons api={api} compact />
          <HistoryButtons api={api} compact />
        </div>

        <div className="phase-bar">
          <div className="phase-bar__step" data-tutorial="stepping">
            <StepButton api={api} direction={-1} className="btn phase-bar__back" title={`Previous ${phaseWord.toLowerCase()} — hold to play backwards`}>
              ◂
            </StepButton>
            <StepButton api={api} direction={1} className="btn btn--primary phase-bar__next" title={`Next ${phaseWord.toLowerCase()} — hold to play on`}>
              Next ▸
            </StepButton>
          </div>
          <div className="phase-bar__chips" data-tutorial="phases">
            <PhaseChips api={api} />
          </div>
        </div>

        <nav ref={tabsRef} className="sheet-tabs" aria-label="More controls" data-tutorial="sheet-tabs">
          {TABS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              className={`sheet-tabs__tab ${tab === id ? 'sheet-tabs__tab--open' : ''}`}
              aria-expanded={tab === id}
              aria-controls={`sheet-${id}`}
              title={id === 'record' && recording.phase === 'recording' ? 'Recording' : undefined}
              onClick={() => toggle(id)}
            >
              {label}
              {id === 'record' && recording.phase === 'recording' && <span className="record__dot" aria-hidden="true" />}
            </button>
          ))}
        </nav>
      </main>

      <Sheet tab={tab} onClose={() => setTab(null)} clear={clear}>
        <SheetPage id="ink" open={tab === 'ink'} title="Ink">
          <InkControls api={api} />
        </SheetPage>
        <SheetPage id="setup" open={tab === 'setup'} title="Setup">
          <SetupControls api={api} formations={formations} phone />
        </SheetPage>
        <SheetPage id="record" open={tab === 'record'}>
          {/* Kept mounted with the sheet shut: the recording lives on. */}
          <RecordPanel recording={recording}>
            <SavedPlays plays={plays} />
          </RecordPanel>
        </SheetPage>
        <SheetPage id="more" open={tab === 'more'} title="More">
          <div className="toolbar__row">
            <button type="button" className="btn" onClick={() => api.seek(0)}>
              ⏮ Back to the start
            </button>
          </div>
          <Scrubber api={api} />
          <DeletePhaseButton api={api} />
          {developer && <ExportControls {...exports} />}
          <PackHint api={api} />
        </SheetPage>
      </Sheet>
    </>
  );
}

interface SheetProps {
  readonly tab: SheetTab | null;
  readonly onClose: () => void;
  /** Room left free for the tabs: below the sheet upright, to its right sideways. */
  readonly clear: { readonly bottom: number; readonly right: number };
  readonly children: ReactNode;
}

/**
 * Slides up over the lower board in portrait and in from the right in
 * landscape. Closed by a tap on the board around it, a swipe down on its
 * handle, its tab again, or Escape.
 */
function Sheet({ tab, onClose, clear, children }: SheetProps): JSX.Element {
  const [drag, setDrag] = useState(0);
  const start = useRef<{ id: number; y: number } | null>(null);
  const open = tab !== null;

  return (
    <div className={`sheet ${open ? 'sheet--open' : ''}`} style={{ bottom: clear.bottom, right: clear.right } as CSSProperties}>
      <div className="sheet__backdrop" onClick={onClose} aria-hidden="true" />
      <section
        className="sheet__panel"
        aria-hidden={!open}
        style={drag > 0 ? { transform: `translateY(${drag}px)`, transition: 'none' } : undefined}
      >
        <div
          className="sheet__handle"
          onPointerDown={(event) => {
            start.current = { id: event.pointerId, y: event.clientY };
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            if (start.current?.id !== event.pointerId) return;
            setDrag(Math.max(0, event.clientY - start.current.y));
          }}
          onPointerUp={(event) => {
            if (start.current?.id !== event.pointerId) return;
            const dragged = event.clientY - start.current.y;
            start.current = null;
            setDrag(0);
            // A swipe down closes; a tap on the handle closes too.
            if (dragged > SWIPE_CLOSE_PX || Math.abs(dragged) < 6) onClose();
          }}
          onPointerCancel={() => {
            start.current = null;
            setDrag(0);
          }}
        >
          <span className="sheet__grip" aria-hidden="true" />
          <button type="button" className="sheet__close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="sheet__body">{children}</div>
      </section>
    </div>
  );
}

interface SheetPageProps {
  readonly id: SheetTab;
  readonly open: boolean;
  readonly title?: string;
  readonly children: ReactNode;
}

function SheetPage({ id, open, title, children }: SheetPageProps): JSX.Element {
  return (
    <div id={`sheet-${id}`} className="sheet__page" hidden={!open}>
      {title && <h2 className="toolbar__heading">{title}</h2>}
      {children}
    </div>
  );
}

interface RecordingPillProps {
  readonly recording: RecordingApi;
  /** False while the Record tab itself is open: it says all of this already. */
  readonly visible: boolean;
  readonly videoReady: boolean;
  readonly onOpen: () => void;
}

/** What a recording is doing, over the board while its panel is out of sight. */
function RecordingPill({ recording, visible, videoReady, onOpen }: RecordingPillProps): JSX.Element | null {
  if (!visible) return null;
  if (recording.phase === 'recording') {
    return (
      <div className="record-pill" role="status">
        <span className="record__dot" aria-hidden="true" />
        <span className="record-pill__clock">{formatClock(recording.elapsed)}</span>
        <button type="button" className="btn btn--danger record-pill__stop" onClick={() => void recording.stop()}>
          ■ Stop
        </button>
      </div>
    );
  }
  if (recording.phase === 'preparing') {
    return (
      <button type="button" className="record-pill" onClick={onOpen}>
        Preparing your video… {Math.round(recording.progress * 100)}%
      </button>
    );
  }
  // Preparing failed even after starting over (#87): the recording is kept,
  // and the coach can try again from here without opening the sheet.
  if (recording.error && recording.canRetry) {
    return (
      <button type="button" className="record-pill record-pill--failed" onClick={() => void recording.retry()}>
        Video couldn’t be prepared — Try again
      </button>
    );
  }
  if (videoReady) {
    return (
      <button type="button" className="record-pill record-pill--ready" onClick={onOpen}>
        Video ready — share
      </button>
    );
  }
  return null;
}
